/**
 * Guards de acceso al panel administrativo (Sprint 10.23).
 *
 * AdminGuard            → admin OR cualquier sub-rol del panel
 *                         (alcanza para mostrar el shell /admin/*).
 *
 * CapabilityGuard       → guard genérico que valida una capability específica
 *                         antes de mostrar una ruta concreta. Acepta admin
 *                         (super-role) o el sub-rol exacto solicitado.
 *
 * Wrappers para uso cómodo:
 *   ModeratorGuard, SupportAgentGuard, PartnerGuard, ContentEditorGuard.
 *
 * Patrón: leemos user_roles vía useUserRoles (mismo cache que el resto del
 * sistema). No usamos query separada para evitar duplicar fetches.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '@/lib/auth';
import { useUserRoles, useCapabilities, type UserCapabilities } from '@/lib/hooks/useUserRoles';
import type { AdminCapability } from '@gurumba/schemas';

function LoadingFullScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
    </div>
  );
}

function NoAccess({ message }: { message?: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center max-w-md">
        <h2 className="text-lg font-semibold">Acceso restringido</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {message ?? 'No tienes permisos para acceder a esta sección.'}
        </p>
      </div>
    </div>
  );
}

/** Guard de entrada al panel: admin OR cualquier sub-rol administrativo. */
export function AdminGuard({ children }: { children: React.ReactNode }) {
  const user = useUser();
  const { isLoading } = useUserRoles();
  const caps = useCapabilities();

  if (!user) return <Navigate to="/login" replace />;
  if (isLoading) return <LoadingFullScreen />;
  if (!caps.hasAnyAdminAccess) {
    return (
      <NoAccess message="Esta sección es solo para administradores y miembros del equipo." />
    );
  }
  return <>{children}</>;
}

/** Guard genérico por capability. Si el user no tiene la capability requerida
 *  Y tampoco es admin, mostramos un mensaje de no-acceso. */
export function CapabilityGuard({
  capability,
  children,
}: {
  capability: AdminCapability;
  children: React.ReactNode;
}) {
  const user = useUser();
  const { isLoading } = useUserRoles();
  const caps = useCapabilities();

  if (!user) return <Navigate to="/login" replace />;
  if (isLoading) return <LoadingFullScreen />;

  const allowed =
    caps.isAdmin ||
    (capability === 'moderator' && caps.isModerator) ||
    (capability === 'support_agent' && caps.isSupportAgent) ||
    (capability === 'partner_viewer' && caps.isPartnerViewer) ||
    (capability === 'partner_finance' && caps.isPartnerFinance) ||
    (capability === 'content_editor' && caps.isContentEditor);

  if (!allowed) {
    return (
      <NoAccess message="Esta sección requiere un permiso específico que tu cuenta no tiene." />
    );
  }
  return <>{children}</>;
}

// Wrappers de conveniencia
export const ModeratorGuard = (props: { children: React.ReactNode }) => (
  <CapabilityGuard capability="moderator">{props.children}</CapabilityGuard>
);
export const SupportAgentGuard = (props: { children: React.ReactNode }) => (
  <CapabilityGuard capability="support_agent">{props.children}</CapabilityGuard>
);
export const PartnerGuard = (props: { children: React.ReactNode }) => (
  <CapabilityGuard capability="partner_viewer">{props.children}</CapabilityGuard>
);
export const ContentEditorGuard = (props: { children: React.ReactNode }) => (
  <CapabilityGuard capability="content_editor">{props.children}</CapabilityGuard>
);
export const PartnerFinanceGuard = (props: { children: React.ReactNode }) => (
  <CapabilityGuard capability="partner_finance">{props.children}</CapabilityGuard>
);

// =====================================================================
// AdminRouteGate (24-sep-2026)
// =====================================================================
// Antes, AdminGuard dejaba entrar a CUALQUIER sub-rol a TODAS las rutas
// /admin/* (el sidebar solo escondía links). Un socio podía abrir
// /admin/founders a mano y ver el botón "Cerrar beta". Ahora cada ruta declara
// qué permiso exige; lo que no está en la lista es solo admin. Si el usuario
// no puede ver la ruta, lo llevamos a la primera que sí puede ver (por ej. el
// socio de finanzas que entra a /admin cae en /admin/finanzas).
type RouteRequirement = AdminCapability | 'admin' | 'any';

const ADMIN_ROUTE_RULES: Array<{ match: (path: string) => boolean; requires: RouteRequirement }> = [
  { match: (p) => p === '/admin' || p === '/admin/', requires: 'partner_viewer' },
  { match: (p) => p.startsWith('/admin/traccion'), requires: 'partner_viewer' },
  { match: (p) => p.startsWith('/admin/finanzas'), requires: 'partner_finance' },
  { match: (p) => p.startsWith('/admin/courses'), requires: 'content_editor' },
  { match: (p) => p.startsWith('/admin/achievements'), requires: 'content_editor' },
  { match: (p) => p.startsWith('/admin/community-moderation'), requires: 'moderator' },
  { match: (p) => p.startsWith('/admin/tickets'), requires: 'support_agent' },
  { match: (p) => p.startsWith('/admin/leads'), requires: 'support_agent' },
  { match: (p) => p.startsWith('/admin/notifications'), requires: 'any' },
];

function allows(req: RouteRequirement, caps: UserCapabilities): boolean {
  if (caps.isAdmin) return true;
  switch (req) {
    case 'any':             return caps.hasAnyAdminAccess;
    case 'admin':           return false;
    case 'partner_viewer':  return caps.isPartnerViewer;
    case 'partner_finance': return caps.isPartnerFinance;
    case 'content_editor':  return caps.isContentEditor;
    case 'moderator':       return caps.isModerator;
    case 'support_agent':   return caps.isSupportAgent;
    default:                return false;
  }
}

/** Primera pantalla que este usuario sí puede ver dentro del panel. */
export function firstAllowedAdminPath(caps: UserCapabilities): string | null {
  if (caps.isAdmin || caps.isPartnerViewer) return '/admin';
  if (caps.isPartnerFinance) return '/admin/finanzas';
  if (caps.isContentEditor) return '/admin/courses';
  if (caps.isModerator) return '/admin/community-moderation';
  if (caps.isSupportAgent) return '/admin/tickets';
  return null;
}

export function AdminRouteGate({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { isLoading } = useUserRoles();
  const caps = useCapabilities();

  if (isLoading) return <LoadingFullScreen />;

  const rule = ADMIN_ROUTE_RULES.find((r) => r.match(pathname));
  const requirement: RouteRequirement = rule?.requires ?? 'admin';
  if (allows(requirement, caps)) return <>{children}</>;

  const fallback = firstAllowedAdminPath(caps);
  if (fallback && fallback !== pathname) return <Navigate to={fallback} replace />;
  return <NoAccess message="Esta sección requiere un permiso específico que tu cuenta no tiene." />;
}
